"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { Clock, Mail, Phone } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const hubs = [{
  code: "HUB / 01 / NYC",
  name: "New York, NY",
  region: "NORTHEAST OPS",
  address: ["482 Greenwich Street", "Suite 4B", "New York, NY 10013"],
  phone: "(212) 555-0142",
  email: "nyc@haulx.com",
  hours: "Mon–Fri 8a–6p",
  status: "OPEN NOW",
  manager: "John Smith",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694695/img/manager_nyc_hub.png"
}, {
  code: "HUB / 02 / BOS",
  name: "Boston, MA",
  region: "NORTHEAST OPS",
  address: ["219 Harrison Avenue", "Unit 12", "Boston, MA 02111"],
  phone: "(617) 555-0188",
  email: "bos@haulx.com",
  hours: "Mon–Fri 8a–6p",
  status: "OPEN NOW",
  manager: "Lisa Johnson",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694695/img/manager_bos_hub.png"
}, {
  code: "HUB / 03 / PHL",
  name: "Philadelphia, PA",
  region: "MID-ATLANTIC",
  address: ["1100 N Delaware Ave", "Building C", "Philadelphia, PA 19125"],
  phone: "(215) 555-0167",
  email: "phl@haulx.com",
  hours: "Mon–Fri 8a–6p",
  status: "OPEN NOW",
  manager: "Marcus Reed",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694695/img/manager_phl_hub.png"
}, {
  code: "HUB / 04 / DC",
  name: "Washington, DC",
  region: "MID-ATLANTIC",
  address: ["1455 V Street NW", "Floor 2", "Washington, DC 20009"],
  phone: "(202) 555-0119",
  email: "dc@haulx.com",
  hours: "Mon–Fri 8a–6p",
  status: "OPEN NOW",
  manager: "Priya Nair",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694695/img/manager_dc_hub.png"
}, {
  code: "HUB / 05 / ATL",
  name: "Atlanta, GA",
  region: "SOUTHEAST OPS",
  address: ["650 North Avenue NE", "Bay 7", "Atlanta, GA 30308"],
  phone: "(404) 555-0173",
  email: "atl@haulx.com",
  hours: "Mon–Fri 8a–6p",
  status: "OPEN NOW",
  manager: "Devon Carter",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694695/img/manager_atl_hub.png"
}, {
  code: "HUB / 06 / CHI",
  name: "Chicago, IL",
  region: "MIDWEST OPS",
  address: ["2540 W Fulton Street", "Dock 3", "Chicago, IL 60612"],
  phone: "(312) 555-0124",
  email: "chi@haulx.com",
  hours: "Mon–Fri 8a–6p",
  status: "OPEN NOW",
  manager: "Hannah Beck",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694695/img/manager_chi_hub.png"
}, {
  code: "HUB / 07 / DEN",
  name: "Denver, CO",
  region: "MOUNTAIN WEST",
  address: ["3401 Brighton Boulevard", "Suite 200", "Denver, CO 80216"],
  phone: "(303) 555-0156",
  email: "den@haulx.com",
  hours: "Mon–Fri 8a–6p",
  status: "OPEN NOW",
  manager: "Theo Alvarez",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694695/img/manager_den_hub.png"
}, {
  code: "HUB / 08 / LAX",
  name: "Los Angeles, CA",
  region: "WEST COAST",
  address: ["1933 S Broadway", "Warehouse B", "Los Angeles, CA 90007"],
  phone: "(213) 555-0198",
  email: "lax@haulx.com",
  hours: "Mon–Fri 8a–6p",
  status: "OPEN NOW",
  manager: "Naomi Park",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1694695/img/manager_lax_hub.png"
}];
export default function ServiceLocations() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: pillRef,
    isVisible: pillVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const firstRow = hubs.slice(0, 4);
  const secondRow = hubs.slice(4, 8);
  return;
}
type Hub = {
  code: string;
  name: string;
  region: string;
  address: string[];
  phone: string;
  email: string;
  hours: string;
  status: string;
  manager: string;
  avatar: string;
};
function HubCard({
  hub,
  index,
  dataIndex
}: {
  hub: Hub;
  index: number;
  dataIndex: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const initials = hub.manager.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
  const telHref = `tel:${hub.phone.replace(/[^0-9+]/g, "")}`;
  const mailHref = `mailto:${hub.email}`;
  return <div ref={ref} data-index={dataIndex} style={{
    transitionDelay: `${index * 60}ms`
  }} className={`group relative bg-card border border-border rounded-md p-7 flex flex-col aspect-[4/5] transition-all duration-700 ease-out hover:-translate-y-0.5 hover:border-primary/30 shadow-sm ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
      {}
      <div className="flex items-center justify-between">
        <span className="font-mono text-[11px] tracking-[0.15em] text-muted-foreground group-hover:text-primary transition-colors">
          {hub.code}
        </span>
        <span className="inline-block size-1.5 rounded-lg bg-primary animate-pulse" />
      </div>

      {}
      <div className="mt-5">
        <h3 className="font-serif font-default text-[26px] leading-tight tracking-tight text-foreground">
          {hub.name}
        </h3>
        <p className="mt-1.5 font-mono text-[10px] tracking-[0.18em] uppercase text-muted-foreground">
          {hub.region}
        </p>
      </div>

      {}
      <div className="my-5 h-px w-full bg-border" />

      {}
      <div className="space-y-4 flex-1">
        <div>
          <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-muted-foreground/60 mb-1.5">
            Address
          </p>
          <div className="font-sans text-[13px] leading-relaxed text-foreground/80">
            {hub.address.map((line, i) => <div key={i} data-index={i}>
                {line}
              </div>)}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Phone className="size-3.5 text-muted-foreground shrink-0" />
          <Link to={telHref} className="font-serif font-default text-[17px] text-foreground hover:text-primary transition-colors">
            {hub.phone}
          </Link>
        </div>

        <div className="flex items-center gap-2">
          <Mail className="size-3.5 text-muted-foreground shrink-0" />
          <Link to={mailHref} className="font-mono text-[12px] text-foreground/75 hover:text-primary transition-colors break-all">
            {hub.email}
          </Link>
        </div>

        <div className="flex items-center gap-2">
          <Clock className="size-3.5 text-muted-foreground shrink-0" />
          <span className="font-mono text-[12px] text-foreground/75">
            {hub.hours}
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          <span className="inline-block size-1.5 rounded-lg bg-chart-5" />
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-muted-foreground">
            {hub.status}
          </span>
        </div>
      </div>

      {}
      <div className="my-5 h-px w-full bg-border" />

      {}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <SafeHydrate><Avatar className="size-8 rounded-md border border-border">
            <AvatarImage src={hub.avatar} alt={hub.manager} />
            <AvatarFallback className="rounded-md bg-muted text-muted-foreground text-[10px] font-mono">
              {initials}
            </AvatarFallback>
          </Avatar></SafeHydrate>
          <span className="font-sans text-[12px] text-foreground/80 truncate">
            {hub.manager}
          </span>
        </div>
        <Link to={telHref} className="font-mono text-[11px] tracking-[0.1em] text-primary hover:text-accent transition-colors whitespace-nowrap">
          Direct dial →
        </Link>
      </div>
    </div>;
}